import { logInternalError } from './errors';

export interface AutocompleteSuggestion {
  placeId: string;
  description: string;
}

export interface PlaceAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

interface PlacesAutocompletePrediction {
  placePrediction?: {
    placeId: string;
    text?: { text: string };
  };
}

interface PlacesAutocompleteResponse {
  suggestions?: PlacesAutocompletePrediction[];
}

export interface AutocompleteResult {
  suggestions: AutocompleteSuggestion[];
  /**
   * True when the request itself failed (rejected key, network, quota) rather than
   * simply matching nothing. Callers use this to stop offering a search box that
   * cannot work instead of leaving the customer poking at a dead control.
   */
  unavailable: boolean;
}

const PLACES_API_BASE = 'https://places.googleapis.com/v1';

/**
 * Fetches address autocomplete suggestions from google places (new) API via REST.
 * Uses REST instead of the JS SDK to avoid conflicts with stripe's own google maps
 * script loaded for PaymentElement.
 */
export async function fetchAutocompleteSuggestions(
  input: string,
  country: string,
  apiKey: string,
  sessionToken: string,
): Promise<AutocompleteResult> {
  if (!input.trim()) return { suggestions: [], unavailable: false };

  const response = await fetch(`${PLACES_API_BASE}/places:autocomplete`, {
    method: 'POST',
    headers: {
      'X-Goog-Api-Key': apiKey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      input,
      includedRegionCodes: [country.toLowerCase()],
      sessionToken,
    }),
  });

  if (!response.ok) {
    logInternalError(new Error(`google places autocomplete failed: ${response.status}`), {
      tags: { googlePlacesEndpoint: 'autocomplete', statusCode: response.status },
    });
    return { suggestions: [], unavailable: true };
  }

  const data: PlacesAutocompleteResponse = await response.json();
  const suggestions = (data.suggestions ?? [])
    .filter((s): s is Required<PlacesAutocompletePrediction> => !!s.placePrediction)
    .map((s) => ({
      placeId: s.placePrediction.placeId,
      description: s.placePrediction.text?.text ?? '',
    }));
  return { suggestions, unavailable: false };
}

/**
 * Fetches full address details for a place ID. Maps google address components
 * to stripe's address format (line1, line2, city, state, postalCode, country).
 */
export async function fetchPlaceDetails(
  placeId: string,
  apiKey: string,
  sessionToken: string,
): Promise<PlaceAddress | null> {
  const params = new URLSearchParams({
    key: apiKey,
    fields: 'addressComponents,formattedAddress',
    sessionToken,
  });
  const response = await fetch(`${PLACES_API_BASE}/places/${placeId}?${params}`);

  if (!response.ok) {
    logInternalError(new Error(`google places details failed: ${response.status}`), {
      tags: { googlePlacesEndpoint: 'placeDetails', statusCode: response.status },
    });
    return null;
  }

  const data = await response.json();
  const components: Array<{ types: string[]; longText?: string; shortText?: string }> = data.addressComponents ?? [];

  const get = (type: string) => components.find((c) => c.types.includes(type));

  const streetNumber = get('street_number')?.longText ?? '';
  const route = get('route')?.longText ?? '';
  const subpremise = get('subpremise')?.longText ?? '';
  const locality =
    get('locality')?.longText ??
    get('postal_town')?.longText ??
    get('administrative_area_level_3')?.longText ??
    get('administrative_area_level_2')?.longText ??
    '';
  const adminArea = get('administrative_area_level_1')?.shortText ?? '';
  const postalCode = get('postal_code')?.longText ?? '';
  const countryComponent = get('country')?.shortText ?? '';

  return {
    // european format: street name before number (this path only runs for non-stripe countries)
    line1: [route, streetNumber].filter(Boolean).join(' '),
    line2: subpremise,
    city: locality,
    state: adminArea,
    postalCode,
    country: countryComponent,
  };
}
